import React from 'react'
import { observer } from 'mobx-react-lite'
import { AttachmentLoader, type IColumn, Typography } from 'shared/ui'
import { DayjsFormats, formatBytes } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { EmptyCell } from 'shared/ui/Table'
import { type KnowledgeBaseDocument, KnowledgeBaseDocumentStatus } from 'entities/KnowledgeBase'
import { FileIcon } from 'widgets/FileIcon/FileIcon'
import {
  KnowledgeBaseDocumentStatusLabel,
  getKnowledgeBaseDocumentStatusConfig,
} from 'widgets/KnowledgeBase'
import { UNSUPPORTED_FORMATS } from 'pages/settings/pages/knowledgeBase/lib/constants'
import styles from './styles.module.scss'

export const columns: IColumn<KnowledgeBaseDocument>[] = [
  {
    field: 'name',
    name: 'Name',
    width: '100%',
    minWidth: '145px',
    maxWidth: '245px',
    isDisabledChangeVisible: true,
    renderRowCell: (row) => <NameCell file={row} />,
    onCheckCellPointer: (row) => isSupportedFormat(row),
  },
  {
    field: 'url',
    name: 'URL',
    renderRowCell: (row) =>
      row.importUrl ? (
        <Typography
          variant={'body-md-regular'}
          link
          textDecoration={'none'}
          ariaLabel={'Import URL'}
        >
          <a
            href={row.importUrl}
            target='_blank'
            rel='noopener noreferrer'
            onClick={(e) => e.stopPropagation()}
          >
            {row.importUrl}
          </a>
        </Typography>
      ) : (
        <EmptyCell />
      ),
    minWidth: 180,
    onCheckCellPointer: (row) => isSupportedFormat(row),
  },
  {
    field: 'status',
    name: 'Status',
    isDisabledChangeVisible: true,
    renderRowCell: (row) => <StatusCell file={row} />,
    minWidth: 110,
    onCheckCellPointer: (row) => isSupportedFormat(row),
  },
  {
    field: 'size',
    name: 'Size',
    fullName: 'Size',
    isDisabledChangeVisible: true,
    renderRowCell: (row) => (row.size ? formatBytes(row.size) : <EmptyCell />),
    minWidth: 60,
    onCheckCellPointer: (row) => isSupportedFormat(row),
  },
  {
    field: 'createdTime',
    name: 'Uploaded time',
    fullName: 'Uploaded time',
    renderRowCell: (row) =>
      row.createdAt ? (
        uiStore.dayjs(row.createdAt).format(DayjsFormats.fullWithAtDash2)
      ) : (
        <EmptyCell />
      ),
    minWidth: 180,
    onCheckCellPointer: (row) => isSupportedFormat(row),
  },
  {
    field: 'uploadedAt',
    name: 'Last updated',
    renderRowCell: (row) =>
      row.uploadedAt ? (
        uiStore.dayjs(row.uploadedAt).format(DayjsFormats.fullWithAtDash2)
      ) : (
        <EmptyCell />
      ),
    minWidth: 180,
    onCheckCellPointer: (row) => isSupportedFormat(row),
  },
]

export const isSupportedFormat = (row: KnowledgeBaseDocument) =>
  !UNSUPPORTED_FORMATS.includes(row.contentType)

const NameCell = observer(({ file }: { file: KnowledgeBaseDocument }) => {
  const isUploading = file.status === KnowledgeBaseDocumentStatus.Uploading
  const isWebUrl = file.source === 'web_url'
  const docType = isWebUrl ? 'web_url' : file.extension || file.contentType

  return (
    <div className={styles.nameContainer}>
      <div className={styles.iconContainer}>
        <FileIcon docType={docType} fontSize={20} />
        {isUploading && <AttachmentLoader isLibrary />}
      </div>
      <Typography
        variant='body-md-regular'
        ariaLabel={`KnowledgeBaseDocument_${file.name}`}
        className={styles.name}
      >
        {file.name}
      </Typography>
    </div>
  )
})

const StatusCell = observer(({ file }: { file: KnowledgeBaseDocument }) =>
  getKnowledgeBaseDocumentStatusConfig(file.status) ? (
    <KnowledgeBaseDocumentStatusLabel status={file.status} />
  ) : (
    <EmptyCell />
  )
)
